import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage, injectIntl, useIntl } from "react-intl";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import * as Utils from "../../commons/utils/Utils";
import { Table, TableContainer, TableHead, TableRow, TableBody, Tooltip, Collapse } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import { StyledTableCellProps as StyledTableCell } from "../../commons/utils/TableStyles";
import LocaleFormat from "../../commons/utils/LocaleFormat.js";
import ConfigBusiness from "../../commons/config/ConfigBusiness.js";
import Security from "../../commons/security/Security";
import * as ESTADOS from "../../commons/constants/estadoIntentoSecuencia.js";
import GridFilterPanel from "../../commons/filters/GridFilterPanel.jsx";
import GridSearchResultsBadge from "../../commons/filters/GridSearchResultsBadge.jsx";
import GridPagination from "../../commons/grid/GridPagination.jsx";
import GridColumnSelector from "../../commons/grid/GridColumnSelector.jsx";
import useColumnVisibility from "../../commons/grid/useColumnVisibility.js";
import { GridToolbar, GridToolbarButton } from "../../commons/grid/GridToolbar.jsx";
import LimpiarFiltros from "../../assets/images/limpiar-filtros.svg";
import FormularioSecuenciaIntentoIniciarModal from "./FormularioSecuenciaIntentoIniciarModal.jsx";
import { SEGUIMIENTO_INGRESOS_FILTROS_STORAGE_KEY, buildSeguimientoIngresosFilterFields, aParametrosDeApi } from "./seguimientoIngresosFilterFields.js";
import { SEGUIMIENTO_INGRESOS_COLUMNAS_STORAGE_KEY, buildSeguimientoIngresosColumns } from "./seguimientoIngresosColumns.js";

const MySwal = withReactContent(Swal);

const POR_PAGINA_INICIAL = 25;

// Celdas más justas que las del tema: con 8 columnas la tabla no entraba en pantallas de 1366 px.
const CELDA = { padding: "10px 8px" };
// La barra de avance ya no lleva número al lado: sin aire a la derecha queda pegada al "1 / 3" de la columna Intento y se lee como una sola cosa.
const CELDA_AVANCE = Object.assign({}, CELDA, { paddingRight: 32 });

// La fila abierta y su detalle comparten fondo y una franja del color del estado en el borde izquierdo, para que se lea que son una sola unidad.
// La franja es un box-shadow interno: los bordes de las celdas los anula una regla global del tema.
const FONDO_ABIERTO = "#eef0fa";

// Etiqueta de color de cada estado, como en el mockup: en curso amarillo, rechazado rojo, aprobado verde, descalificado gris.
const ESTILO_ESTADO = {
  [ESTADOS.GRILLA_EN_CURSO]: { badge: "badge-warning", borde: "#f0ad4e" },
  [ESTADOS.GRILLA_RECHAZADO]: { badge: "badge-danger", borde: "#d9534f" },
  [ESTADOS.GRILLA_APROBADO]: { badge: "badge-success", borde: "#5cb85c" },
  [ESTADOS.GRILLA_DESCALIFICADO]: { badge: "badge-secondary", borde: "#9a9aa8" },
};

const getEstadoLabel = (estado, intl) => {
  switch (estado) {
    case ESTADOS.GRILLA_EN_CURSO:
      return intl.formatMessage({ id: "formularioSecuenciaIntentoGrid.estado.en_curso", defaultMessage: "En Curso" });
    case ESTADOS.GRILLA_RECHAZADO:
      return intl.formatMessage({ id: "formularioSecuenciaIntentoGrid.estado.rechazado", defaultMessage: "Rechazado" });
    case ESTADOS.GRILLA_APROBADO:
      return intl.formatMessage({ id: "formularioSecuenciaIntentoGrid.estado.aprobado", defaultMessage: "Aprobado" });
    case ESTADOS.GRILLA_DESCALIFICADO:
      return intl.formatMessage({ id: "formularioSecuenciaIntentoGrid.estado.descalificado", defaultMessage: "Descalificado" });
    default:
      return "";
  }
};

// RECHAZADO no se ofrece: la grilla muestra el intento actual de cada vehículo y, al rechazarse uno, el siguiente se abre solo (los rechazados quedan en el historial).
const ESTADOS_FILTRABLES = [ESTADOS.GRILLA_EN_CURSO, ESTADOS.GRILLA_APROBADO, ESTADOS.GRILLA_DESCALIFICADO];

// Con tantos checklists los segmentos dejan de ser legibles y la barra vuelve a ser continua.
const MAX_SEGMENTOS = 15;
const COLOR_SEGMENTO = { [ESTADOS.CHECKLIST_APROBADO]: "#5cb85c", [ESTADOS.CHECKLIST_RECHAZADO]: "#d9534f", [ESTADOS.CHECKLIST_PENDIENTE]: "#e6e6ee", [ESTADOS.CHECKLIST_EN_VALIDACION]: "#f0ad4e" };

// Orden de lectura de la barra y del detalle: aprobados, rechazados y pendientes.
const PESO_BARRA = { [ESTADOS.CHECKLIST_APROBADO]: 0, [ESTADOS.CHECKLIST_RECHAZADO]: 1, [ESTADOS.CHECKLIST_EN_VALIDACION]: 2, [ESTADOS.CHECKLIST_PENDIENTE]: 3 };

/** Barra de avance: un trozo por checklist (verde aprobado, rojo rechazado, ámbar esperando al supervisor, gris pendiente). El "7 de 10" se lee al pasar el mouse. */
const BarraAvance = ({ aprobados, total, rechazado, checklists }) => {
  const intl = useIntl();
  const lista = checklists || [];
  const segmentada = lista.length > 1 && lista.length <= MAX_SEGMENTOS;
  const porcentaje = total > 0 ? Math.round((aprobados * 100) / total) : 0;
  const ordenados = lista.slice().sort((a, b) => PESO_BARRA[a.estado] - PESO_BARRA[b.estado]);
  const rechazados = lista.filter((c) => c.estado === ESTADOS.CHECKLIST_RECHAZADO).length;
  // Al pasar el mouse la barra se explica sola, sin tener que abrir la fila para ver la leyenda.
  const enValidacion = lista.filter((c) => c.estado === ESTADOS.CHECKLIST_EN_VALIDACION).length;
  const partes = [
    intl.formatMessage(
      { id: "formularioSecuenciaIntentoGrid.avance.tooltip", defaultMessage: "{aprobados} de {total} checklists aprobados" },
      { aprobados: aprobados, total: total }
    ),
  ];
  if (rechazados > 0) {
    partes.push(
      intl.formatMessage(
        { id: "formularioSecuenciaIntentoGrid.avance.tooltip_cant_rechazados", defaultMessage: "{rechazados, plural, one {# rechazado} other {# rechazados}}" },
        { rechazados: rechazados }
      )
    );
  }
  if (enValidacion > 0) {
    partes.push(
      intl.formatMessage(
        { id: "formularioSecuenciaIntentoGrid.avance.tooltip_en_validacion", defaultMessage: "{cantidad} esperando al supervisor" },
        { cantidad: enValidacion }
      )
    );
  }
  const explicacion = partes.join(" · ");
  return (
    // El Tooltip de MUI trae letra de 10px; acá es lo único que dice la columna, así que va más grande.
    <Tooltip title={<span style={{ display: "block", fontSize: "1rem", lineHeight: 1.4, padding: "6px 10px" }}>{explicacion}</span>} arrow placement="top">
      <div style={{ display: "flex", alignItems: "center", minWidth: 120 }}>
        <div style={{ flex: "1 1 auto", height: 7, borderRadius: 4, background: segmentada ? "transparent" : "#e6e6ee", overflow: "hidden", display: "flex", gap: segmentada ? 2 : 0 }}>
          {segmentada ? (
            ordenados.map((c) => <div key={c.tipo_formulario_id} style={{ flex: 1, height: "100%", borderRadius: 2, background: COLOR_SEGMENTO[c.estado] }}></div>)
          ) : (
            <div style={{ width: porcentaje + "%", height: "100%", background: rechazado ? "#d9534f" : "#5cb85c" }}></div>
          )}
        </div>
      </div>
    </Tooltip>
  );
};

const iconoChecklist = (estado) => {
  switch (estado) {
    case ESTADOS.CHECKLIST_APROBADO:
      return <i className="la la-check-circle" style={{ color: "#28a745", fontSize: "1.7rem" }}></i>;
    case ESTADOS.CHECKLIST_RECHAZADO:
      return <i className="la la-times-circle" style={{ color: "#dc3545", fontSize: "1.7rem" }}></i>;
    case ESTADOS.CHECKLIST_EN_VALIDACION:
      return <i className="la la-hourglass-half" style={{ color: "#e69a28", fontSize: "1.7rem" }}></i>;
    default:
      return <i className="la la-circle" style={{ color: "#b4b4c0", fontSize: "1.7rem" }}></i>;
  }
};

// Íconos de la leyenda: los mismos de los recuadros, en chico.
const iconoLeyenda = (estado) => {
  const colores = { [ESTADOS.CHECKLIST_APROBADO]: "#28a745", [ESTADOS.CHECKLIST_RECHAZADO]: "#dc3545", [ESTADOS.CHECKLIST_PENDIENTE]: "#b4b4c0", [ESTADOS.CHECKLIST_EN_VALIDACION]: "#e69a28" };
  const clases = { [ESTADOS.CHECKLIST_APROBADO]: "la-check-circle", [ESTADOS.CHECKLIST_RECHAZADO]: "la-times-circle", [ESTADOS.CHECKLIST_PENDIENTE]: "la-circle", [ESTADOS.CHECKLIST_EN_VALIDACION]: "la-hourglass-half" };
  return <i className={"la " + clases[estado]} style={{ color: colores[estado], fontSize: "1.2rem", verticalAlign: "middle" }}></i>;
};

/** Leyenda de los íconos de los recuadros. Va dentro del desplegable, que es donde aparecen. */
/** `mostrarEspera`: la entrada "esperando al supervisor" solo tiene sentido con la validación del supervisor habilitada (o si ya hay alguno esperando). */
const LeyendaChecklists = ({ mostrarEspera }) => (
  <div className="text-muted d-flex flex-wrap align-items-center" style={{ gap: 18, fontSize: "0.8rem", marginTop: 10 }}>
    <span>
      {iconoLeyenda(ESTADOS.CHECKLIST_APROBADO)} <FormattedMessage id="formularioSecuenciaIntentoGrid.leyenda.aprobado" defaultMessage="Checklist aprobado" />
    </span>
    <span>
      {iconoLeyenda(ESTADOS.CHECKLIST_RECHAZADO)} <FormattedMessage id="formularioSecuenciaIntentoGrid.leyenda.rechazado" defaultMessage="Checklist rechazado" />
    </span>
    {mostrarEspera && (
      <span>
        {iconoLeyenda(ESTADOS.CHECKLIST_EN_VALIDACION)}{" "}
        <FormattedMessage id="formularioSecuenciaIntentoGrid.leyenda.en_validacion" defaultMessage="Esperando validación del supervisor" />
      </span>
    )}
    <span>
      {iconoLeyenda(ESTADOS.CHECKLIST_PENDIENTE)} <FormattedMessage id="formularioSecuenciaIntentoGrid.leyenda.pendiente" defaultMessage="Checklist pendiente" />
    </span>
  </div>
);

// Recuadro de cada checklist: borde verde si está aprobado, rojo y fondo rosado si se rechazó, gris si falta.
const ESTILO_RECUADRO = {
  [ESTADOS.CHECKLIST_APROBADO]: { border: "1px solid #28a745", background: "#ffffff" },
  [ESTADOS.CHECKLIST_RECHAZADO]: { border: "1px solid #dc3545", background: "#fdeaec" },
  [ESTADOS.CHECKLIST_EN_VALIDACION]: { border: "1px solid #f0ad4e", background: "#fff8e6" },
  [ESTADOS.CHECKLIST_PENDIENTE]: { border: "1px solid #e1e1ea", background: "#ffffff" },
};

/** Línea de abajo del recuadro: qué pasó con el checklist, cuándo y quién lo hizo. Los pendientes no llevan línea: el círculo vacío ya lo dice. */
const textoDelChecklist = (checklist, intl) => {
  if (checklist.estado === ESTADOS.CHECKLIST_PENDIENTE) {
    return "";
  }
  let resultado;
  if (checklist.estado === ESTADOS.CHECKLIST_APROBADO) {
    resultado = intl.formatMessage({ id: "formularioSecuenciaIntentoGrid.detalle.checklist_aprobado", defaultMessage: "Aprobado" });
  } else if (checklist.estado === ESTADOS.CHECKLIST_EN_VALIDACION) {
    resultado = intl.formatMessage({ id: "formularioSecuenciaIntentoGrid.detalle.checklist_en_validacion", defaultMessage: "Esperando validación del supervisor" });
  } else {
    resultado = intl.formatMessage({ id: "formularioSecuenciaIntentoGrid.detalle.checklist_rechazado", defaultMessage: "Rechazado" });
  }
  return [resultado, checklist.fecha_resultado ? LocaleFormat.formatDateTime(checklist.fecha_resultado) : null, checklist.realizado_por]
    .filter(Boolean)
    .join(" · ");
};

/** Detalle de la fila, como el mockup: una tarjeta del intento con un recuadro por checklist y una nota con lo que pasó o lo que falta. */
// Desde esta cantidad de checklists los aprobados se resumen en una línea que se despliega; los rechazados y pendientes siguen a la vista.
const UMBRAL_PLEGAR_APROBADOS = 6;
// Con más que esto la lista tiene su propio scroll.
const MAX_CHECKLISTS_SIN_SCROLL = 15;

// Primero lo que requiere atención: rechazados, después pendientes y al final los aprobados (cada grupo en el orden de la secuencia).
const PESO_DETALLE = { [ESTADOS.CHECKLIST_RECHAZADO]: 0, [ESTADOS.CHECKLIST_EN_VALIDACION]: 1, [ESTADOS.CHECKLIST_PENDIENTE]: 2, [ESTADOS.CHECKLIST_APROBADO]: 3 };

const RecuadroChecklist = ({ c, intl, rechazadoAntes }) => {
  const marco = ESTILO_RECUADRO[c.estado] || ESTILO_RECUADRO[ESTADOS.CHECKLIST_PENDIENTE];
  return (
    <div style={Object.assign({ display: "flex", alignItems: "center", borderRadius: 4, padding: "8px 12px", minWidth: 0 }, marco)}>
      {iconoChecklist(c.estado)}
      <div className="ml-1" style={{ minWidth: 0, flex: "1 1 auto" }}>
        <div title={c.nombre} style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {c.nombre}
        </div>
        {c.estado !== ESTADOS.CHECKLIST_PENDIENTE && (
          <small className="text-muted" style={{ display: "block", whiteSpace: "normal", wordBreak: "break-word" }}>
            {textoDelChecklist(c, intl)}
          </small>
        )}
        {rechazadoAntes && c.estado === ESTADOS.CHECKLIST_PENDIENTE && (
          <small style={{ display: "block", whiteSpace: "normal", color: "#dc3545" }}>
            <i className="la la-undo" style={{ verticalAlign: "middle" }}></i>{" "}
            <FormattedMessage id="formularioSecuenciaIntentoGrid.detalle.rechazado_antes" defaultMessage="Rechazado en el intento anterior" />
          </small>
        )}
        {c.conservado && (
          <small className="text-muted" style={{ display: "block", whiteSpace: "normal" }}>
            <FormattedMessage id="formularioSecuenciaIntentoGrid.detalle.conservado" defaultMessage="Aprobado en el intento anterior" />
          </small>
        )}
      </div>
    </div>
  );
};

/** Tarjeta de un intento: encabezado con el número, las fechas y el estado, un recuadro por checklist y, si corresponde, una nota de lo que pasó. */
// `rechazadosAntes`: ids de los checklists que se rechazaron en el intento anterior (solo se pasa en la tarjeta del intento actual).
const TarjetaIntento = ({ intento, intl, rechazadosAntes }) => {
  const [verAprobados, setVerAprobados] = useState(false);
  const rechazado = intento.checklists.find((c) => c.estado === ESTADOS.CHECKLIST_RECHAZADO);
  const quedan = intento.cantidad_maxima_intentos - intento.numero_intento;
  const estilo = ESTILO_ESTADO[intento.estado] || ESTILO_ESTADO[ESTADOS.GRILLA_DESCALIFICADO];

  let nota = "";
  if (intento.estado === ESTADOS.GRILLA_APROBADO) {
    nota = intl.formatMessage({ id: "formularioSecuenciaIntentoGrid.detalle.todos_aprobados", defaultMessage: "Todos los checklists aprobados." });
  } else if (intento.estado === ESTADOS.GRILLA_DESCALIFICADO) {
    nota = intl.formatMessage(
      { id: "formularioSecuenciaIntentoGrid.detalle.descalificado", defaultMessage: "Rechazó {checklist}: el intento terminó y no quedan intentos disponibles." },
      { checklist: rechazado ? rechazado.nombre : "" }
    );
  } else if (intento.estado === ESTADOS.GRILLA_RECHAZADO) {
    nota = intl.formatMessage(
      { id: "formularioSecuenciaIntentoGrid.detalle.rechazado", defaultMessage: "Rechazó {checklist}: el intento terminó. Quedan {quedan} intentos." },
      { checklist: rechazado ? rechazado.nombre : "", quedan: quedan }
    );
  }

  const ordenados = intento.checklists.slice().sort((x, y) => PESO_DETALLE[x.estado] - PESO_DETALLE[y.estado]);
  const plegar = intento.checklists.length > UMBRAL_PLEGAR_APROBADOS;
  const aprobados = ordenados.filter((c) => c.estado === ESTADOS.CHECKLIST_APROBADO);
  const visibles = plegar && !verAprobados ? ordenados.filter((c) => c.estado !== ESTADOS.CHECKLIST_APROBADO) : ordenados;
  const conScroll = intento.checklists.length > MAX_CHECKLISTS_SIN_SCROLL;

  return (
    <div style={{ background: "#ffffff", border: "1px solid #e8e8f0", borderRadius: 4, padding: "12px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div>
          <strong>
            <FormattedMessage
              id="formularioSecuenciaIntentoGrid.detalle.intento_n_de_m"
              defaultMessage="Intento {numero} de {maximo}"
              values={{ numero: intento.numero_intento, maximo: intento.cantidad_maxima_intentos }}
            />
          </strong>
          <span className="text-muted ml-1">
            {"· " + (intento.fecha_inicio ? LocaleFormat.formatDateTime(intento.fecha_inicio) : "") + (intento.fecha_fin ? " - " + LocaleFormat.formatDateTime(intento.fecha_fin) : "")}
          </span>
        </div>
        <span className={"badge " + estilo.badge}>{getEstadoLabel(intento.estado, intl)}</span>
      </div>
      {plegar && aprobados.length > 0 && (
        <div className="text-muted" style={{ cursor: "pointer", marginBottom: 8, fontSize: "0.85rem" }} onClick={() => setVerAprobados(!verAprobados)}>
          <i className="la la-check-circle" style={{ color: "#28a745", fontSize: "1.2rem", verticalAlign: "middle" }}></i>{" "}
          <FormattedMessage id="formularioSecuenciaIntentoGrid.detalle.resumen_aprobados" defaultMessage="{cantidad} aprobados" values={{ cantidad: aprobados.length }} />
          {" · "}
          <span style={{ textDecoration: "underline" }}>
            {verAprobados ? (
              <FormattedMessage id="formularioSecuenciaIntentoGrid.detalle.ocultar_aprobados" defaultMessage="ocultar" />
            ) : (
              <FormattedMessage id="formularioSecuenciaIntentoGrid.detalle.ver_aprobados" defaultMessage="ver" />
            )}
          </span>
        </div>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 10,
          maxHeight: conScroll ? 360 : "none",
          overflowY: conScroll ? "auto" : "visible",
        }}
      >
        {visibles.map((c) => (
          <RecuadroChecklist key={c.tipo_formulario_id} c={c} intl={intl} rechazadoAntes={!!rechazadosAntes && rechazadosAntes.indexOf(c.tipo_formulario_id) >= 0} />
        ))}
      </div>
      {nota && (
        <div className="text-muted" style={{ fontSize: "0.85rem", marginTop: 8 }}>
          {nota}
        </div>
      )}
    </div>
  );
};

/**
 * Detalle de la fila: la tarjeta del intento actual y, si el vehículo tuvo intentos anteriores (rechazados), un botón "Historial de intentos"
 * que los despliega, del más reciente al más viejo.
 */
const DetalleIntento = ({ intento, intl }) => {
  const [verHistorial, setVerHistorial] = useState(false);
  const historial = intento.historial || [];
  // Mismo criterio que el motor de secuencias en la API: solo con la clave en 'true'.
  const validacionDelSupervisor = ConfigBusiness.get("checklist.validacion.granular.habilitado") === "true";
  // El intento actual nació del rechazo del anterior (el más reciente del historial): ese checklist es lo que hay que rehacer.
  const rechazadosAntes = historial.length > 0 ? historial[0].checklists.filter((c) => c.estado === ESTADOS.CHECKLIST_RECHAZADO).map((c) => c.tipo_formulario_id) : [];

  // Las celdas de esta tabla heredan white-space: pre; sin volver a "normal" los textos largos del detalle (la nota del intento, el nombre y la persona
  // de cada recuadro) no se parten en líneas y se salen de la pantalla (scroll horizontal).
  return (
    <div style={{ padding: "12px 16px 16px 16px", whiteSpace: "normal" }}>
      <TarjetaIntento intento={intento} intl={intl} rechazadosAntes={rechazadosAntes} />
      {historial.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setVerHistorial(!verHistorial)}>
            <i className={"la " + (verHistorial ? "la-angle-up" : "la-history")} style={{ verticalAlign: "middle" }}></i>{" "}
            {verHistorial ? (
              <FormattedMessage id="formularioSecuenciaIntentoGrid.detalle.ocultar_historial" defaultMessage="Ocultar historial" />
            ) : (
              <FormattedMessage
                id="formularioSecuenciaIntentoGrid.detalle.ver_historial"
                defaultMessage="Historial de intentos ({cantidad})"
                values={{ cantidad: historial.length }}
              />
            )}
          </button>
          {verHistorial && (
            <div style={{ marginTop: 10 }}>
              {historial.map((anterior) => (
                <div key={anterior.id} style={{ marginBottom: 10 }}>
                  <TarjetaIntento intento={anterior} intl={intl} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <LeyendaChecklists mostrarEspera={validacionDelSupervisor || [intento].concat(historial).some((i) => i.checklists.some((c) => c.estado === ESTADOS.CHECKLIST_EN_VALIDACION))} />
    </div>
  );
};

const FormularioSecuenciaIntentoGrid = (props) => {
  const intl = props.intl;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(POR_PAGINA_INICIAL);
  const [secuencias, setSecuencias] = useState([]);
  const [query, setQuery] = useState({});
  const [expandidos, setExpandidos] = useState({});
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);
  const [resetSignal, setResetSignal] = useState(false);
  // Columnas que el usuario eligió ver (se recuerda por usuario, por nombre de columna).
  const columnasGrilla = useColumnVisibility(buildSeguimientoIngresosColumns(), SEGUIMIENTO_INGRESOS_COLUMNAS_STORAGE_KEY);
  const visibles = columnasGrilla.visibles;
  const cantidadVisibles = Object.keys(visibles).filter((nombre) => visibles[nombre]).length;
  // Un pedido viejo no debe pisar a uno más nuevo (filtrar o paginar rápido).
  const pedidoVigente = useRef(0);
  const primerApply = useRef(true);

  const fields = buildSeguimientoIngresosFilterFields();
  const filtrosActivos = Object.keys(aParametrosDeApi(query)).length > 0;

  const fetchIntentos = (queryActual, paginaActual, porPagina) => {
    const pedido = ++pedidoVigente.current;
    const params = Object.assign({}, aParametrosDeApi(queryActual), { page: paginaActual + 1, perPage: porPagina });
    const qs = Object.keys(params)
      .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(params[k]))
      .join("&");

    setLoading(true);
    Utils.getData("/formulario-secuencia-intento/grid?" + qs)
      .then((res) => {
        if (pedido !== pedidoVigente.current) return;
        setRows(res.rows || []);
        setCount(res.count || 0);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        if (pedido === pedidoVigente.current) setLoading(false);
      });
  };

  useEffect(() => {
    Utils.getData("/tipo-formulario-secuencia")
      .then((res) => setSecuencias(res || []))
      .catch((err) => console.log(err));
  }, []);

  const handleApply = (nuevaQuery) => {
    const completa = nuevaQuery || {};
    // Primer apply (el que hace el panel al montar con lo que tenía guardado): si hay filtros, se abre el panel para no dejar al usuario mirando un
    // listado filtrado sin ver por qué.
    if (primerApply.current && Object.keys(aParametrosDeApi(completa)).length > 0) setFiltrosAbiertos(true);
    primerApply.current = false;
    setQuery(completa);
    setPage(0);
    setExpandidos({});
    fetchIntentos(nuevaQuery || {}, 0, perPage);
  };

  // LIMPIAR LA BÚSQUEDA: la "✕" de la franja de resultados. Vuelve los filtros al default y no toca las columnas.
  const handleReset = () => {
    try {
      localStorage.removeItem(Security.claveDeUsuario(SEGUIMIENTO_INGRESOS_FILTROS_STORAGE_KEY));
    } catch (e) {}
    setQuery({});
    setPage(0);
    setExpandidos({});
    setResetSignal(true);
    fetchIntentos({}, 0, perPage);
  };

  const handleChangePage = (event, nuevaPagina) => {
    setPage(nuevaPagina);
    setExpandidos({});
    fetchIntentos(query, nuevaPagina, perPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const nueva = parseInt(event.target.value, 10);
    setPerPage(nueva);
    setPage(0);
    setExpandidos({});
    fetchIntentos(query, 0, nueva);
  };

  // RESTAURAR: el botón del toolbar. Además de limpiar la búsqueda cierra el panel y vuelve a mostrar todas las columnas.
  const handleRestaurar = () => {
    setFiltrosAbiertos(false);
    columnasGrilla.mostrarTodas();
    handleReset();
  };

  const toggleDetalle = (id) => {
    const copia = Object.assign({}, expandidos);
    copia[id] = !copia[id];
    setExpandidos(copia);
  };

  const eventNuevoIntento = () => {
    MySwal.fire({
      title: intl.formatMessage({ id: "formularioSecuenciaIntentoGrid.modal_nuevo_intento.titulo", defaultMessage: "Iniciar validación de secuencia" }),
      html: (
        <FormularioSecuenciaIntentoIniciarModal
          intl={intl}
          secuencias={secuencias.filter((secuencia) => secuencia.activo)}
          alIniciar={() => fetchIntentos(query, page, perPage)}
          success={() => MySwal.clickConfirm()}
          dismiss={() => MySwal.clickCancel()}
        />
      ),
      showConfirmButton: false,
    });
  };

  const options = {
    secuencia: secuencias.map((s) => ({ value: s.id, label: s.nombre })),
    estado: ESTADOS_FILTRABLES.map((e) => ({ value: e, label: getEstadoLabel(e, intl) })),
  };

  return (
    <>
      <div className="card">
        <div className="card-content">
          <div className="card-body">
            <div className="container-fluid">
              {/* Los botones de la derecha (Restaurar y columnas) son más altos que la fila: sin este margen quedan pegados al encabezado de la tabla, a diferencia de tickets. */}
              <div className="row buttons-grid dt-icons" style={{ marginBottom: 25 }}>
                <div className="col d-flex">
                  {Security.hasPermission("CHECKLIST_INICIAR_SECUENCIA") && (
                    <Tooltip
                      title={intl.formatMessage({ id: "formularioSecuenciaIntentoGrid.button.label_nuevo_intento", defaultMessage: "Nuevo intento" })}
                      arrow
                      placement="right"
                    >
                      <div className="btn btn-primary box-shadow-2 btn-round btn-lg btn-dt-main round-icon" onClick={eventNuevoIntento}>
                        <i className="ft-plus"></i>
                      </div>
                    </Tooltip>
                  )}
                  <div
                    className="btn btn-grey-blue btn-round box-shadow-2 btn-dt-main round-icon"
                    onClick={() => setFiltrosAbiertos(!filtrosAbiertos)}
                    title={intl.formatMessage({ id: "Filtros", defaultMessage: "Filtros" })}
                  >
                    {filtrosAbiertos ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </div>
                </div>
                <div className="col">
                  <GridSearchResultsBadge visible={filtrosActivos} mostrados={rows.length} total={count} onReset={handleReset} />
                </div>
                <div className="col">
                  <GridToolbar>
                    <GridToolbarButton
                      messageId="secuencias.tools_restore_filters.title_restaurar_filtros_y_columnas"
                      label="Restaurar filtros y columnas"
                      onClick={handleRestaurar}
                    >
                      <img id="filterReset" className="filter-reset" src={LimpiarFiltros} alt="" />
                    </GridToolbarButton>
                    <GridColumnSelector columns={columnasGrilla.seleccionables} esVisible={columnasGrilla.esVisible} onToggle={columnasGrilla.toggle} />
                  </GridToolbar>
                </div>
              </div>
            </div>

            <GridFilterPanel
              open={filtrosAbiertos}
              fields={fields}
              options={options}
              storageKey={SEGUIMIENTO_INGRESOS_FILTROS_STORAGE_KEY}
              loading={loading}
              resetSignal={resetSignal}
              onResetSignalHandled={() => setResetSignal(false)}
              onApply={handleApply}
              applyOnMount
            />

            <TableContainer>
              <Table aria-label="seguimiento de ingresos table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell align="left" style={CELDA}>
                      <FormattedMessage id="formularioSecuenciaIntentoGrid.column_MOVIL.label" defaultMessage="Vehículo" />
                    </StyledTableCell>
                    <StyledTableCell align="left" show={visibles.secuencia} style={CELDA}>
                      <FormattedMessage id="formularioSecuenciaIntentoGrid.column_SECUENCIA.label" defaultMessage="Secuencia" />
                    </StyledTableCell>
                    <StyledTableCell align="left" show={visibles.estado} style={CELDA}>
                      <FormattedMessage id="formularioSecuenciaIntentoGrid.column_ESTADO.label" defaultMessage="Estado" />
                    </StyledTableCell>
                    <StyledTableCell align="left" show={visibles.avance} style={CELDA_AVANCE}>
                      <FormattedMessage id="formularioSecuenciaIntentoGrid.column_AVANCE.label" defaultMessage="Avance" />
                    </StyledTableCell>
                    <StyledTableCell align="left" show={visibles.intento} style={CELDA}>
                      <FormattedMessage id="formularioSecuenciaIntentoGrid.column_INTENTO.label" defaultMessage="Intento" />
                    </StyledTableCell>
                    <StyledTableCell align="left" show={visibles.fechaInicio} style={CELDA}>
                      <FormattedMessage id="formularioSecuenciaIntentoGrid.column_FECHA_INICIO.label" defaultMessage="Fecha inicio" />
                    </StyledTableCell>
                    <StyledTableCell align="left" show={visibles.fechaFin} style={CELDA}>
                      <FormattedMessage id="formularioSecuenciaIntentoGrid.column_FECHA_FIN.label" defaultMessage="Fecha fin" />
                    </StyledTableCell>
                    <StyledTableCell align="left" style={CELDA}></StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <StyledTableCell colSpan={cantidadVisibles} align="center">
                        <FormattedMessage id="Cargando" defaultMessage="Cargando" />
                      </StyledTableCell>
                    </TableRow>
                  )}
                  {!loading && rows.length === 0 && (
                    <TableRow>
                      <StyledTableCell colSpan={cantidadVisibles} align="center">
                        <FormattedMessage id="formularioSecuenciaIntentoGrid.no_data_table" defaultMessage="No hay intentos de validación registrados..." />
                      </StyledTableCell>
                    </TableRow>
                  )}
                  {!loading &&
                    rows.map((intento) => {
                      const abierto = !!expandidos[intento.id];
                      const estilo = ESTILO_ESTADO[intento.estado] || ESTILO_ESTADO[ESTADOS.GRILLA_DESCALIFICADO];
                      const rechazado = intento.estado === ESTADOS.GRILLA_RECHAZADO || intento.estado === ESTADOS.GRILLA_DESCALIFICADO;
                      return (
                        <React.Fragment key={"formularioSecuenciaIntentoGridRow_" + intento.id}>
                          <TableRow hover style={{ cursor: "pointer", background: abierto ? FONDO_ABIERTO : undefined }} onClick={() => toggleDetalle(intento.id)}>
                            <StyledTableCell align="left" style={abierto ? Object.assign({}, CELDA, { boxShadow: "inset 5px 0 0 " + estilo.borde }) : CELDA}>
                              {intento.movil ? intento.movil.dominio : ""}
                            </StyledTableCell>
                            <StyledTableCell show={visibles.secuencia} align="left" style={Object.assign({}, CELDA, { whiteSpace: "normal", maxWidth: 240 })}>
                              {intento.secuencia ? intento.secuencia.nombre : ""}
                            </StyledTableCell>
                            <StyledTableCell show={visibles.estado} align="left" style={CELDA}>
                              <span className={"badge " + estilo.badge}>{getEstadoLabel(intento.estado, intl)}</span>
                            </StyledTableCell>
                            <StyledTableCell show={visibles.avance} align="left" style={CELDA_AVANCE}>
                              <BarraAvance aprobados={intento.avance.aprobados} total={intento.avance.total} rechazado={rechazado} checklists={intento.checklists} />
                            </StyledTableCell>
                            <StyledTableCell show={visibles.intento} align="left" style={CELDA}>
                              {intento.numero_intento} / {intento.cantidad_maxima_intentos}
                              {intento.historial && intento.historial.length > 0 && (
                                <i
                                  className="la la-history text-muted ml-1"
                                  style={{ verticalAlign: "middle" }}
                                  title={intl.formatMessage(
                                    {
                                      id: "formularioSecuenciaIntentoGrid.intento.con_historial",
                                      defaultMessage: "Tuvo {cantidad, plural, one {# intento anterior} other {# intentos anteriores}}",
                                    },
                                    { cantidad: intento.historial.length }
                                  )}
                                ></i>
                              )}
                            </StyledTableCell>
                            <StyledTableCell show={visibles.fechaInicio} align="left" style={CELDA}>{intento.fecha_inicio ? LocaleFormat.formatDateTime(intento.fecha_inicio) : ""}</StyledTableCell>
                            <StyledTableCell show={visibles.fechaFin} align="left" style={CELDA}>{intento.fecha_fin ? LocaleFormat.formatDateTime(intento.fecha_fin) : <span className="text-muted">—</span>}</StyledTableCell>
                            <StyledTableCell align="right" style={CELDA}>{abierto ? <ExpandLessIcon /> : <ExpandMoreIcon />}</StyledTableCell>
                          </TableRow>
                          <TableRow>
                            <StyledTableCell
                              colSpan={cantidadVisibles}
                              style={
                                abierto
                                  ? { padding: 0, background: FONDO_ABIERTO, boxShadow: "inset 5px 0 0 " + estilo.borde + ", inset 0 -2px 0 " + estilo.borde }
                                  : { padding: 0, border: "none" }
                              }
                            >
                              <Collapse in={abierto} timeout="auto" unmountOnExit>
                                <DetalleIntento intento={intento} intl={intl} />
                              </Collapse>
                            </StyledTableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <GridPagination count={count} page={page} perPage={perPage} onPageChange={handleChangePage} onPerPageChange={handleChangeRowsPerPage} />
          </div>
        </div>
      </div>
    </>
  );
};

export default injectIntl(FormularioSecuenciaIntentoGrid);
